// ── Enums ──

export enum VaultRole {
  Owner   = 1,
  Spouse  = 2,
  Witness = 3,
  Advisor = 4,
}

export enum LetterState {
  Draft    = 0,
  Sealed   = 1,
  Unsealed = 2,
}

export enum VaultStatus {
  Active = 1,
  PastDue = 2,
  Suspended = 3,
  ReadOnly = 4,
}

export enum AccountTier {
  Trial = 1,
  Paid = 2,
  ForeverFree = 3,
}

export enum DocumentCategory {
  Will = 1,
  Trust = 2,
  Insurance = 3,
  Medical = 4,
  Financial = 5,
  Photo = 6,
  Video = 7,
  Other = 8,
  Deed = 9,
  PowerOfAttorney = 10,
  BirthCertificate = 11,
  MarriageCertificate = 12,
  DeathCertificate = 13,
  SocialSecurity = 14,
  TaxRecord = 15,
  FuneralInstructions = 16,
  PersonalLetter = 17,
  Membership = 18,
}


export enum FinancialAccountType {
  Checking      = 1,
  Savings       = 2,
  Brokerage     = 3,
  IRA           = 4,
  RothIRA       = 5,
  FourOhOneK    = 6,
  Pension       = 7,
  LifeInsurance = 8,
  CreditCard    = 9,
  Mortgage      = 10,
  Other         = 11,
  OnlineAccount = 12,
}

// ── Auth DTOs ──

export interface RegisterRequest {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  invitationToken?: string;
  foreverFreeToken?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  expiresAt: string;
  email: string;
  firstName: string;
  lastName: string;
  requires2FA: boolean;
  isAdmin: boolean;
}

export interface RefreshResponse {
  accessToken: string;
  expiresAt: string;
}

export interface ConfirmEmailRequest {
  userId: string;
  token: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  email: string;
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export interface TwoFactorStatusResponse {
  isEnabled: boolean;
  hasAuthenticator: boolean;
  recoveryCodesLeft: number;
}

export interface Enable2FAResponse {
  sharedKey: string;
  authenticatorUri: string;
}

export interface Verify2FARequest {
  code: string;
}

export interface Verify2FAResponse {
  message: string;
  recoveryCodes: string[];
}

export interface Disable2FARequest {
  password: string;
}

export interface TwoFactorLoginRequest {
  email: string;
  code: string;
  useRecoveryCode: boolean;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// ── Vault DTOs ──

export interface CreateVaultRequest {
  name: string;
  acceptedTermsId: string;
}

// ── Terms & Conditions DTOs ──

export interface TermsAndConditionsResponse {
  id: string;
  version: string;
  content: string;
  effectiveDate: string;
}

export interface TermsVersionSummary {
  id: string;
  version: string;
  effectiveDate: string;
  createdAt: string;
  isActive: boolean;
  acceptanceCount: number;
}

export interface TermsAcceptanceAuditEntry {
  userId: string;
  userEmail: string;
  userName: string;
  acceptedAt: string;
  ipAddress: string | null;
}

export interface SaveAdminNotesRequest {
  notes: string | null;
}

export interface AdminMessageRequest {
  subject: string;
  body: string;
}

export interface CreateTermsRequest {
  version: string;
  content: string;
  effectiveDateUtc: string;
}

export interface UpdateVaultRequest {
  name: string;
}

export interface VaultMemberResponse {
  membershipId: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: VaultRole;
  invitedAt: string;
  acceptedAt: string | null;
}

export interface VaultResponse {
  id: string;
  name: string;
  status: VaultStatus;
  accountTier: AccountTier;
  createdAt: string;
  deactivatedAt: string | null;
  trialEndsAt: string | null;
  scheduledDeletionAt: string | null;
  ownerId: string;
  members: VaultMemberResponse[];
}

export interface VaultSummaryResponse {
  id: string;
  name: string;
  status: VaultStatus;
  accountTier: AccountTier;
  createdAt: string;
  trialEndsAt: string | null;
  userRole: VaultRole;
  memberCount: number;
  ownerName: string;
}

// ── Billing DTOs ──

export interface BillingStatusResponse {
  accountTier: AccountTier;
  vaultStatus: VaultStatus;
  trialEndsAt: string | null;
  scheduledDeletionAt: string | null;
  stripeSubscriptionStatus: string | null;
  hasActiveSubscription: boolean;
}

export interface StartCheckoutRequest {
  billingInterval: 'monthly' | 'yearly';
}

export interface CheckoutSessionResponse {
  checkoutUrl: string;
}

export interface CustomerPortalResponse {
  portalUrl: string;
}

export interface ReadinessItem {
  label: string;
  description: string;
  met: boolean;
  points: number;
  tab: string | null;
}

export interface VaultCountsResponse {
  profiles: number;
  contacts: number;
  funeralPlans: number;
  documents: number;
  heirlooms: number;
  notes: number;
  letters: number;
  videos: number;
  financialAccounts: number;
}

// ── Financial Account DTOs ──

export interface CreateFinancialAccountRequest {
  institutionName: string;
  accountType: FinancialAccountType;
  accountNumber?: string | null;
  contactPhone?: string | null;
  website?: string | null;
  notes?: string | null;
}

export interface UpdateFinancialAccountRequest extends CreateFinancialAccountRequest {}

export interface FinancialAccountResponse {
  id: string;
  vaultId: string;
  institutionName: string;
  accountType: FinancialAccountType;
  /** True when an account number is stored — always set regardless of witness lock state. */
  hasAccountNumber: boolean;
  accountNumber: string | null;
  contactPhone: string | null;
  website: string | null;
  notes: string | null;
  sortOrder: number;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string | null;
}

// ── Witness Access Vote DTOs ──

/** One cast vote within a witness password-access vote session. */
export interface AccessVoteCastResponse {
  vaultMemberId: string;
  /** First name only — matches sealed-letter vote chip style. */
  memberName: string | null;
  vote: boolean;
  castAt: string;
}

/**
 * Current state of an open witness password-access vote.
 * Embedded in FinancialAccountListResponse so the page loads in one round-trip.
 */
export interface WitnessAccessVoteResponse {
  hasActiveRequest: boolean;
  /** Full name — displayed as "Rebekkah Barrett has requested access…" */
  requestedByName: string | null;
  requestedByMemberId: string | null;
  requestedAt: string | null;
  votes: AccessVoteCastResponse[];
  eligibleVoterCount: number;
  yesCount: number;
  noCount: number;
}

export interface CastAccessVoteRequest {
  vote: boolean;
}

export interface ToggleWitnessAccessRequest {
  value: boolean;
}

export interface FinancialAccountListResponse {
  accounts: FinancialAccountResponse[];
  totalCount: number;
  /** Whether Witnesses can currently reveal account numbers. Always true for Owner/Spouse. */
  witnessPasswordAccess: boolean;
  /** Active unlock vote request, if any. Null when no vote is in progress. */
  activeAccessVote: WitnessAccessVoteResponse | null;
}

export interface EstateReadinessResponse {
  score: number;
  items: ReadinessItem[];
}

export interface InviteMemberRequest {
  email: string;
  role: VaultRole;
}

// ── Invitation DTOs ──

export interface InvitationDetailResponse {
  vaultName: string;
  role: string;
  inviterName: string;
  invitedEmail: string;
  maskedEmail: string;
  expiresAt: string;
  isExpired: boolean;
  isAccepted: boolean;
  inviteeHasAccount: boolean;
}

export interface AcceptInvitationRequest {
  acceptedTermsId: string;
}

export interface PendingInvitationResponse {
  id: string;
  email: string;
  role: string;
  invitedAt: string;
  expiresAt: string;
}

export interface AuditLogResponse {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  userEmail: string | null;
  ipAddress: string | null;
  timestamp: string;
}

// ── Document DTOs ──

export interface DocumentResponse {
  id: string;
  vaultId: string;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  category: DocumentCategory;
  sortOrder: number;
  uploadedByUserId: string;
  uploadedAt: string;
}

export interface DocumentListResponse {
  documents: DocumentResponse[];
  totalCount: number;
}

// ── Secure Note DTOs ──

export interface CreateSecureNoteRequest {
  title: string;
  content: string;
}

export interface UpdateSecureNoteRequest {
  title: string;
  content: string;
}

export interface SecureNoteResponse {
  id: string;
  vaultId: string;
  title: string;
  content: string | null;
  sortOrder: number;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string | null;
}

export interface SecureNoteListResponse {
  notes: SecureNoteResponse[];
  totalCount: number;
}

// ── Emergency Contact DTOs ──

export interface CreateEmergencyContactRequest {
  name: string;
  relationship: string;
  phone?: string | null;
  email?: string | null;
  company?: string | null;
  notes?: string | null;
}

export interface UpdateEmergencyContactRequest {
  name: string;
  relationship: string;
  phone?: string | null;
  email?: string | null;
  company?: string | null;
  notes?: string | null;
}

export interface EmergencyContactResponse {
  id: string;
  vaultId: string;
  name: string;
  relationship: string;
  phone: string | null;
  email: string | null;
  company: string | null;
  notes: string | null;
  sortOrder: number;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string | null;
}

export interface EmergencyContactListResponse {
  contacts: EmergencyContactResponse[];
  totalCount: number;
}

// ── Funeral Plan DTOs ──

export interface CreateFuneralPlanRequest {
  personName: string;
  overallWishes?: string | null;
  serviceType?: string | null;
  serviceLocation?: string | null;
  officiant?: string | null;
  musicSelections?: string | null;
  scriptureReadings?: string | null;
  readings?: string | null;
  eulogyBy?: string | null;
  pallbearers?: string | null;
  specialTributes?: string | null;
  dispositionType?: string | null;
  cemeteryName?: string | null;
  cemeteryLocation?: string | null;
  plotInformation?: string | null;
  casketPreference?: string | null;
  vaultPreference?: string | null;
  openClosedCasket?: string | null;
  urnPreference?: string | null;
  remainsDisposition?: string | null;
  viewingWishes?: string | null;
  viewingNotes?: string | null;
  clothingPreference?: string | null;
  personalItems?: string | null;
  flowerPreference?: string | null;
  donationOrganizations?: string | null;
  obituaryWishes?: string | null;
  receptionWishes?: string | null;
  funeralHome?: string | null;
  funeralHomePhone?: string | null;
  prePaidArrangements?: string | null;
  veteranInfo?: string | null;
  organDonation?: string | null;
  additionalWishes?: string | null;
}

export interface UpdateFuneralPlanRequest extends CreateFuneralPlanRequest {}

export interface FuneralPlanResponse {
  id: string;
  vaultId: string;
  personName: string;
  overallWishes: string | null;
  serviceType: string | null;
  serviceLocation: string | null;
  officiant: string | null;
  musicSelections: string | null;
  scriptureReadings: string | null;
  readings: string | null;
  eulogyBy: string | null;
  pallbearers: string | null;
  specialTributes: string | null;
  dispositionType: string | null;
  cemeteryName: string | null;
  cemeteryLocation: string | null;
  plotInformation: string | null;
  casketPreference: string | null;
  vaultPreference: string | null;
  openClosedCasket: string | null;
  urnPreference: string | null;
  remainsDisposition: string | null;
  viewingWishes: string | null;
  viewingNotes: string | null;
  clothingPreference: string | null;
  personalItems: string | null;
  flowerPreference: string | null;
  donationOrganizations: string | null;
  obituaryWishes: string | null;
  receptionWishes: string | null;
  funeralHome: string | null;
  funeralHomePhone: string | null;
  prePaidArrangements: string | null;
  veteranInfo: string | null;
  organDonation: string | null;
  additionalWishes: string | null;
  sortOrder: number;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string | null;
}

export interface FuneralPlanSummary {
  id: string;
  personName: string;
  serviceType: string | null;
  dispositionType: string | null;
  sortOrder: number;
  updatedAt: string | null;
  createdAt: string;
}

export interface FuneralPlanListResponse {
  plans: FuneralPlanSummary[];
  totalCount: number;
}

// ── Heirloom DTOs ──

export interface HeirloomResponse {
  id: string;
  vaultId: string;
  title: string;
  instructions: string;
  hasPhoto: boolean;
  photoContentType: string | null;
  sortOrder: number;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string | null;
}

export interface HeirloomListResponse {
  heirlooms: HeirloomResponse[];
  totalCount: number;
}

export interface UpdateHeirloomRequest {
  title: string;
  instructions: string;
}

// ── Profile DTOs ──

export interface ProfileResponse {
  id: string;
  membershipId: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: VaultRole;
  fullLegalName: string | null;
  preferredName: string | null;
  dateOfBirth: string | null;
  phoneNumber: string | null;
  addressStreet: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressZip: string | null;
  hasPhoto: boolean;
  notes: string | null;
  lastLoginAt: string | null;
}

export interface ProfileListResponse {
  profiles: ProfileResponse[];
  totalCount: number;
}

export interface UpdateProfileRequest {
  fullLegalName?: string | null;
  preferredName?: string | null;
  dateOfBirth?: string | null;
  phoneNumber?: string | null;
  addressStreet?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressZip?: string | null;
  notes?: string | null;
}

// ── Chat DTOs ──

export interface VaultMessageDto {
  id: string;
  vaultId: string;
  sentByUserId: string;
  sentByName: string;
  content: string;
  sentAt: string;
}

export interface ChatHistoryResponse {
  messages: VaultMessageDto[];
  hasMore: boolean;
}

export interface SendMessageRequest {
  content: string;
}

export interface UnreadCountResponse {
  count: number;
}

// ── Generic ──

export interface MessageResponse {
  message: string;
}

export interface ErrorResponse {
  error: string;
}

// ── User display info (cached in sessionStorage) ──

export interface UserInfo {
  email: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
}

// ── Admin DTOs ──

export interface AdminDashboardResponse {
  // Vault status
  totalVaults: number;
  activeVaults: number;
  readOnlyVaults: number;
  suspendedVaults: number;
  // Vault tiers
  trialVaults: number;
  paidVaults: number;
  foreverFreeVaults: number;
  // Revenue — actual paid subscribers
  monthlyRevenue: number;
  annualRunRate: number;
  // Revenue — if all active trials convert
  trialConvertedMrr: number;
  trialConvertedArr: number;
  activeTrialVaults: number;
  // Action queue
  trialsExpiringIn7Days: number;
  lockedOutUsers: number;
  unconfirmedEmailUsers: number;
  staleInvitations: number;
  // Growth
  newUsersThisWeek: number;
  newUsersThisMonth: number;
  // Members
  totalUsers: number;
  totalOwners: number;
  totalSpouses: number;
  totalWitnesses: number;
  // Content
  totalDocuments: number;
  totalNotes: number;
  totalHeirlooms: number;
  totalContacts: number;
  totalFuneralPlans: number;
  totalLetters: number;
  totalVideos: number;
  pendingInvitations: number;
  devFeaturesEnabled: boolean;
}

export interface AdminUserResponse {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  lastLoginAt: string | null;
  has2FA: boolean;
  vaultName: string | null;
  vaultRole: VaultRole | null;
  vaultId: string | null;
  membershipCount: number;
}

export interface AdminVaultResponse {
  id: string;
  name: string;
  status: VaultStatus;
  accountTier: AccountTier;
  createdAt: string;
  ownerEmail: string;
  ownerName: string;
  memberCount: number;
  documentCount: number;
  noteCount: number;
  heirloomCount: number;
  contactCount: number;
  funeralPlanCount: number;
}

export interface AdminActivityResponse {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  userEmail: string | null;
  vaultName: string | null;
  ipAddress: string | null;
  timestamp: string;
}

export interface AdminInvitationResponse {
  id: string;
  email: string;
  role: string;
  vaultName: string;
  inviterName: string;
  invitedAt: string;
  expiresAt: string;
  isExpired: boolean;
  isAccepted: boolean;
  acceptedAt: string | null;
}

export interface ChangeVaultStatusRequest {
  status: VaultStatus;
}

export interface AdminUserDetailResponse {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  lastLoginAt: string | null;
  has2FA: boolean;
  emailConfirmed: boolean;
  isLockedOut: boolean;
  lockoutEnd: string | null;
  accessFailedCount: number;
  memberships: AdminUserVaultMembershipResponse[];
  recentActivity: AdminActivityResponse[];
  adminNotes: string | null;
}

export interface AdminUserVaultMembershipResponse {
  vaultId: string;
  vaultName: string;
  role: VaultRole;
  isOwner: boolean;
  documentCount: number;
  noteCount: number;
  heirloomCount: number;
  contactCount: number;
  funeralPlanCount: number;
}

export interface AdminVaultDetailResponse {
  id: string;
  name: string;
  status: VaultStatus;
  accountTier: AccountTier;
  createdAt: string;
  deactivatedAt: string | null;
  trialEndsAt: string | null;
  scheduledDeletionAt: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripeSubscriptionStatus: string | null;
  ownerId: string;
  ownerEmail: string;
  ownerName: string;
  documentCount: number;
  noteCount: number;
  heirloomCount: number;
  contactCount: number;
  funeralPlanCount: number;
  letterCount: number;
  videoCount: number;
  members: AdminVaultMemberDetailResponse[];
  recentActivity: AdminActivityResponse[];
  adminNotes: string | null;
  videoStorageQuotaOverrideBytes: number | null;
}

export interface AdminVaultMemberDetailResponse {
  userId: string;
  email: string;
  fullName: string;
  role: VaultRole;
  lastLoginAt: string | null;
  has2FA: boolean;
}

export interface DependencyHealthResult {
  ok: boolean;
  detail: string;
  latencyMs?: number;
}

export interface AdminMaintenanceStepResult {
  step: string;
  description: string;
  count: number;
  recordedAt: string;
}

export interface AdminHealthResponse {
  lastMaintenanceRun: string | null;
  lastTrialReminderRun: string | null;
  lastMaintenanceSteps: AdminMaintenanceStepResult[];
  lastTrialReminderSteps: AdminMaintenanceStepResult[];
  blobStorage: DependencyHealthResult;
  emailService: DependencyHealthResult;
  checkedAt: string;
}

// ── Legacy Letter DTOs ──

export interface LetterVoteResponse {
  id: string;
  vaultMemberId: string | null;  // nullable — member may have left the vault
  memberName: string | null;
  vote: boolean;
  castAt: string;
}

export interface LetterResponse {
  id: string;
  vaultId: string;
  addressedTo: string;
  subject: string;
  body: string | null;
  state: LetterState;
  createdByUserId: string | null;
  createdByName: string | null;
  isOwnedByCurrentUser: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string | null;
  sealedAt: string | null;
  unsealedAt: string | null;
  viewerNames: string[];
  totalMembers: number;
  yesVotes: number;
  noVotes: number;
  currentUserVote: boolean | null;
  votes: LetterVoteResponse[];
}

export interface LetterListResponse {
  letters: LetterResponse[];
  totalCount: number;
}

export interface CreateLetterRequest {
  addressedTo: string;
  subject: string;
  body: string;
}

export interface UpdateLetterRequest {
  addressedTo: string;
  subject: string;
  body: string;
}

// ── Advisor ──

export interface AdvisorResponse {
  membershipId: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  advisorTitle: string | null;
  advisorFirm: string | null;
  advisorType: string | null;
  invitedAt: string;
  acceptedAt: string | null;
}

export interface UpdateAdvisorRequest {
  isActive: boolean;
  advisorTitle: string | null;
  advisorFirm: string | null;
  advisorType: string | null;
}

// ── Forever Free DTOs ──

export interface ForeverFreeInvitationResponse {
  id: string;
  email: string;
  sentByAdminName: string;
  sentAt: string;
  expiresAt: string;
  isAccepted: boolean;
  isExpired: boolean;
  acceptedAt: string | null;
  vaultId: string | null;
}

export interface ForeverFreeDetailResponse {
  email: string;        // real email — used for form submission
  maskedEmail: string;  // display only (e.g. r***@laymance.com)
  sentByAdminName: string;
  isAccepted: boolean;
  isExpired: boolean;
}

// ── Legacy Video DTOs ──

export enum VideoState {
  Draft    = 0,
  Sealed   = 1,
  Unsealed = 2,
}

export interface VideoVoteResponse {
  id: string;
  vaultMemberId: string | null;
  memberName: string | null;
  vote: boolean;
  castAt: string;
}

export interface VideoResponse {
  id: string;
  vaultId: string;
  title: string;
  description: string | null;
  originalFileName: string;
  contentType: string;
  fileSizeBytes: number;
  state: VideoState;
  createdByUserId: string | null;
  createdByName: string | null;
  isOwnedByCurrentUser: boolean;
  createdAt: string;
  updatedAt: string | null;
  sealedAt: string | null;
  unsealedAt: string | null;
  isUploadComplete: boolean;
  sortOrder: number;
  /** Only populated in single-item GET responses. Null in list responses. */
  playbackUrl: string | null;
  totalMembers: number;
  yesVotes: number;
  noVotes: number;
  currentUserVote: boolean | null;
  votes: VideoVoteResponse[];
}

export interface VideoListResponse {
  videos: VideoResponse[];
  totalCount: number;
}

export interface VideoUploadTokenResponse {
  videoId: string;
  blobName: string;
  sasUrl: string;
  expiresAt: string;
}

export interface RequestVideoUploadTokenRequest {
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  title: string;
  description?: string | null;
}

export interface UpdateVideoRequest {
  title: string;
  description?: string | null;
}

export interface CastVideoVoteRequest {
  vote: boolean;
}

export interface VideoStorageResponse {
  usedBytes: number;
  quotaBytes: number;
  usedFormatted: string;
  quotaFormatted: string;
  usedPercent: number;
}

// ── Email Family ──

export interface EmailFamilyRequest {
  subject: string;
  message: string;
}

export interface EmailFamilyResponse {
  sent: number;
  failed: number;
  message?: string;
}
